import { useCallback, useRef } from "react";

const DEFAULT_THRESHOLD = 48;

/**
 * Horizontal swipe detection for touch pointers.
 * Returns handlers to spread on a swipeable element and `consumeSwipeClick`
 * to call from onClick when tap should not fire after a swipe.
 */
export function useHorizontalSwipe({
  onSwipeLeft,
  onSwipeRight,
  enabled = true,
  threshold = DEFAULT_THRESHOLD,
} = {}) {
  const startRef = useRef(null);
  const blockClickRef = useRef(false);

  const onPointerDown = useCallback(
    (e) => {
      if (!enabled || e.pointerType === "mouse") return;
      startRef.current = { x: e.clientX, y: e.clientY, id: e.pointerId };
      blockClickRef.current = false;
    },
    [enabled]
  );

  const onPointerMove = useCallback(
    (e) => {
      if (!enabled || !startRef.current || startRef.current.id !== e.pointerId) return;
      const dx = e.clientX - startRef.current.x;
      const dy = e.clientY - startRef.current.y;
      if (Math.abs(dx) >= threshold * 0.45 && Math.abs(dx) > Math.abs(dy) * 1.15) {
        blockClickRef.current = true;
      }
    },
    [enabled, threshold]
  );

  const finishSwipe = useCallback(
    (clientX, clientY) => {
      if (!enabled || !startRef.current) return;
      const dx = clientX - startRef.current.x;
      const dy = clientY - startRef.current.y;
      startRef.current = null;

      if (Math.abs(dx) < threshold || Math.abs(dx) <= Math.abs(dy) * 1.15) return;

      blockClickRef.current = true;
      if (dx < 0) onSwipeLeft?.();
      else onSwipeRight?.();
    },
    [enabled, onSwipeLeft, onSwipeRight, threshold]
  );

  const onPointerUp = useCallback(
    (e) => {
      if (!startRef.current || startRef.current.id !== e.pointerId) return;
      finishSwipe(e.clientX, e.clientY);
    },
    [finishSwipe]
  );

  const onPointerCancel = useCallback(() => {
    startRef.current = null;
  }, []);

  const consumeSwipeClick = useCallback(() => {
    if (!blockClickRef.current) return false;
    blockClickRef.current = false;
    return true;
  }, []);

  return {
    swipeHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
    },
    consumeSwipeClick,
  };
}
